import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ClinicDto, UsersService } from '@anihis/core/data-access';
import { AuthenticationService } from '@anihis/shared/src/services/authentication.service';
import {
  getCountryDisplayName,
  translocoLangToIso,
} from '@anihis/shared/src/countries/countries';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, of, take } from 'rxjs';

@Component({
  selector: 'anihis-clinic-welcome-screen',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './clinic-welcome-screen.component.html',
  styleUrl: './clinic-welcome-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClinicWelcomeScreenComponent implements OnInit {
  private usersService = inject(UsersService);
  private authenticationService = inject(AuthenticationService);
  private translocoService = inject(TranslocoService);

  clinic = signal<ClinicDto | null>(null);
  loading = signal(true);

  ngOnInit(): void {
    const clinicUid = this.authenticationService.getDomainUid();
    if (!clinicUid) {
      this.loading.set(false);
      return;
    }

    this.usersService
      .usersClinicsUidGet(clinicUid)
      .pipe(
        take(1),
        catchError(() => of(null)),
      )
      .subscribe((result) => {
        this.clinic.set(result?.clinic ?? null);
        this.loading.set(false);
      });
  }

  clinicAddress(clinic: ClinicDto): string | null {
    const contact = clinic.contact;
    if (!contact) {
      return null;
    }

    const cityLine = [contact.city]
      .map((part) => part?.trim())
      .filter((part): part is string => !!part)
      .join(' ');

    const parts = [
      contact.street,
      cityLine,
      this.countryDisplayName(contact.country),
    ]
      .map((part) => part?.trim())
      .filter((part): part is string => !!part);

    return parts.length ? parts.join(', ') : null;
  }

  clinicPhone(clinic: ClinicDto): string | null {
    const mobile = clinic.contact?.mobileNumber?.trim();
    return mobile || null;
  }

  private countryDisplayName(value?: string | null): string {
    return getCountryDisplayName(
      value,
      translocoLangToIso(this.translocoService.getActiveLang()),
    );
  }
}
